import * as React from 'react';
import * as UI from 'core/ui';
import { Log } from 'core/infrastructure';
import { Modal } from 'core/ui-next/modal';
import { Box, VStack, ProgressCircle, Text } from '@chakra-ui/react';

type Props = {
	publishClose: (close: () => void) => void,
	title: string;
	message: string
} & Modal.Actions<void>;

function ProcessDialog({ publishClose, message, title, close }: Props) {

	React.useEffect(() => {
		publishClose(close);
	}, [publishClose, close]);

	return <Box bg={{ base: 'white', _dark: '{colors.gray.800}' }}>
		<UI.DialogBody>
			<VStack gap={4} paddingX={4} paddingY={8}>
				<Text fontWeight={'bold'}>{title}</Text>
				<Text fontSize={'sm'} fontWeight={'light'}>{message}</Text>
				<ProgressCircle.Root value={null} size="md">
					<ProgressCircle.Circle>
						<ProgressCircle.Track />
						<ProgressCircle.Range stroke={'pink.600'} strokeLinecap={'round'} />
					</ProgressCircle.Circle>
				</ProgressCircle.Root>
			</VStack>
		</UI.DialogBody>
	</Box>;
}


function show(title: string, message: string, delay: number): Promise<{ close: () => Promise<void> }> {

	Log.debug(`processDialog["${message}"].show()`);

	return new Promise((resolve) => {

		let modalClose: (() => void) | undefined;
		let modalResultPromise: Promise<Result> | undefined;

		const openModal = () => {
			modalResultPromise = Modal.show(ProcessDialog, {
				title,
				message,
				publishClose: close => {
					modalClose = close;
				},
				placement: 'top',
				size: 'sm',
				closeOnInteractOutside: false
			});
		};

		const timer = setTimeout(openModal, delay);

		// Resuelvo hacia fuera en cuanto ya tengo disponible el close, pero exporto
		// un closeAsync para que desde fuera hagan await dialog.close. De esta manera nos
		// aseguramos que el dialogo está cerrado del todo, así si esperamos el close podemos evitar
		// que se ejecuten otros flujos con otros diálogos y rompamos los backdrop de otros.

		resolve({
			close: async () => {
				clearTimeout(timer);
				modalClose?.();

				// Si el modal llegó a abrirse, espero a que termine de cerrarse
				await modalResultPromise;
			}
		});

	});

}
export async function showWhile<T>({ title, msg, action, delay }: { title: string, msg: string, action: () => Promise<T>, delay?: number }): Promise<T> {
	Log.debug(`processDialog["${msg}"].show()`);
	const dialog = await show(title, msg, delay ?? 0);
	try {
		return await action();
	} finally {
		await dialog.close();
		Log.debug(`processDialog["${msg}"].close()`);
	}
}
